import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import MfaChallengeForm from '../components/auth/MfaChallengeForm';
import { translateAuthError } from '../lib/authErrors';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn, UserPlus, User as UserIcon , Loader2 as LoaderCircle} from 'lucide-react';

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signIn, signUp } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [awaitingMfa, setAwaitingMfa] = useState(false);

  // Get redirect information from location state
  const from = location.state?.from || '/';
  const message = location.state?.message || '';

  useEffect(() => {
    if (user && !loading && !awaitingMfa) {
      // Redirect to the page they were trying to access or home
      navigate(from);
    }
  }, [user, loading, awaitingMfa, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    
    try {
      if (isSignUp) {
        // Handle sign up
        if (!username.trim()) {
          setAuthError("Lo username è obbligatorio.");
          setIsSubmitting(false);
          return;
        }
        
        const { error } = await signUp(email, password, username);
        if (error) {
          setAuthError(translateAuthError(error.message));
        } else {
          // Show success message for sign up
          setSuccessMessage("Account creato! Hai effettuato l'accesso.");
          // Redirect to home page after a short delay
          setTimeout(() => {
            navigate('/');
          }, 1500);
        }
      } else {
        // Handle sign in
        const { error } = await signIn(email, password);
        if (error) {
          setAuthError(translateAuthError(error.message));
        } else {
          // Se l'account ha una 2FA registrata, la sessione resta ad aal1
          // finché non completa il challenge: la blocchiamo qui prima del redirect.
          const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
            setAwaitingMfa(true);
          } else {
            // Show success message for sign in
            setSuccessMessage("Accesso effettuato! Ti stiamo reindirizzando...");
            // Redirect to home page after a short delay
            setTimeout(() => {
              navigate(from);
            }, 1000);
          }
        }
      }
    } catch (error: any) {
      setAuthError(translateAuthError(error?.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (awaitingMfa) {
    return (
      <div className="container max-w-md mx-auto py-12">
        <div className="bg-card p-8 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-foreground mb-6">
            Verifica in due passaggi
          </h1>
          <MfaChallengeForm
            onSuccess={() => {
              setAwaitingMfa(false);
              navigate(from);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-md mx-auto py-12">
      <div className="bg-card p-8 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-foreground mb-6">
          {isSignUp ? 'Crea un account' : 'Accedi'}
        </h1>

        {message && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 dark:text-yellow-200">{message}</p>
          </div>
        )}
        
        {authError && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{authError}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <p className="text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {isSignUp && (
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-foreground/80 mb-1">
                Username *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-10 w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Scegli uno username"
                />
              </div>
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground/80 mb-1">
              Email *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                id="email"
                type="email"
                autoComplete={isSignUp ? 'email' : 'username'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="nome@email.it"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground/80 mb-1">
              Password *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                id="password"
                type="password"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
                minLength={isSignUp ? 8 : undefined}
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || !!successMessage}
            className="w-full flex items-center justify-center bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-hover-scale"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <LoaderCircle  size={16} className='animate-spin' />
                Attendere...
              </span>
            ) : isSignUp ? (
              <span className="flex items-center">
                <UserPlus className="h-5 w-5 mr-2" />
                Registrati
              </span>
            ) : (
              <span className="flex items-center">
                <LogIn className="h-5 w-5 mr-2" />
                Accedi
              </span>
            )}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setAuthError(null);
            }}
            className="text-primary hover:text-magenta-600 transition-colors link-hover"
          >
            {isSignUp ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
          </button>
        </div>
        
        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground text-center">
            Accedendo o creando un account accetti le{' '}
            <Link to="/condizioni-di-vendita" className="text-primary hover:text-magenta-600 transition-colors link-hover">
              Condizioni di vendita
            </Link>{' '}
            e confermi di aver letto la{' '}
            <Link to="/privacy" className="text-primary hover:text-magenta-600 transition-colors link-hover">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}